import React, { useState } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  Box,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import { Order, OrderBundleItem } from '../../../store/order/api/interface';
import { useAppSelector } from '../../../store';
import { resolveAssetUrl } from '../../../utils/url';
import OrderBundleExtrasCollapseRows from './OrderBundleExtrasCollapseRows';
import {
  getBundleLineTotal,
  getOrderSpecialRequestText,
} from '../../../utils/orderTotals';

interface Props {
  bundleItem: OrderBundleItem;
  order: Order;
  idx: number;
  showSpecialRequest?: boolean;
  handleOpenLightBox?: (imageObject: string) => void;
}

const OrderBundleItemTableRow = ({
  bundleItem,
  order,
  idx,
  showSpecialRequest = false,
  handleOpenLightBox,
}: Props) => {
  const [open, setOpen] = useState(false);
  const assetUrl = useAppSelector(
    (state) => state.active.selectedItem.assetUrl
  );
  const hasChildren =
    (bundleItem.components?.length ?? 0) > 0 ||
    (bundleItem.variantSelections?.length ?? 0) > 0;
  const bundleQty = Number(bundleItem.qty ?? 1) || 1;
  const bundleUnitPrice = parseFloat(
    Number(bundleItem.unitPrice ?? 0).toFixed(2)
  );
  const rowTotal = parseFloat(Number(getBundleLineTotal(bundleItem)).toFixed(2));
  const bundlePreviewKey = bundleItem.previewUrl || '';
  const bundlePreviewSrc = bundlePreviewKey
    ? resolveAssetUrl(bundlePreviewKey, assetUrl)
    : '';

  return (
    <>
      <TableRow
        sx={{
          borderBottom: (theme) => `solid 1px ${theme.palette.divider}`,
        }}
      >
        <TableCell align='left'>
          {hasChildren && (
            <IconButton
              aria-label='expand bundle row'
              size='small'
              onClick={() => setOpen(!open)}
            >
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          )}
        </TableCell>
        <TableCell>{idx + 1}</TableCell>
        <TableCell align='left'>
          {bundlePreviewSrc ? (
            <Box
              component='img'
              src={bundlePreviewSrc}
              alt={bundleItem.labelEn || 'Bundle product'}
              onClick={
                handleOpenLightBox && bundlePreviewKey
                  ? () => handleOpenLightBox(bundlePreviewKey)
                  : undefined
              }
              sx={{
                width: 48,
                height: 48,
                m: 1,
                objectFit: 'contain',
                cursor: handleOpenLightBox ? 'pointer' : 'default',
                display: 'block',
              }}
            />
          ) : (
            '-'
          )}
        </TableCell>
        <TableCell align='right'>-</TableCell>
        <TableCell align='right'>-</TableCell>
        <TableCell align='right'>-</TableCell>
        <TableCell align='right'>{bundleItem.labelEn || '-'}</TableCell>
        <TableCell align='right'>
          {showSpecialRequest ? getOrderSpecialRequestText(order) : '-'}
        </TableCell>
        <TableCell align='right'>-</TableCell>
        <TableCell align='right'>{bundleQty}</TableCell>
        <TableCell align='right'>{bundleUnitPrice}</TableCell>
        <TableCell align='right'>{rowTotal}</TableCell>
      </TableRow>
      {hasChildren && (
        <TableRow>
          <TableCell
            style={{ paddingBottom: 0, paddingTop: 0 }}
            colSpan={12}
          >
            <Collapse
              in={open}
              timeout='auto'
              unmountOnExit
            >
              <Box sx={{ py: 1 }}>
                <Table
                  size='small'
                  aria-label={`bundle-items-${bundleItem.id}`}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell align='center'>Type</TableCell>
                      <TableCell align='center'>Name</TableCell>
                      <TableCell align='center'>Image</TableCell>
                      <TableCell align='center'>Price</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <OrderBundleExtrasCollapseRows
                      order={order}
                      bundleItem={bundleItem}
                      hideBundleHeader
                      hideLineSubtotal
                      includeCakeComponents
                      handleOpenLightBox={handleOpenLightBox}
                    />
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

export default OrderBundleItemTableRow;
